import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation, withTranslation} from 'react-i18next';
import moment from 'moment';
import settingsActions from '../../../Actions/settingsActions'
import commonActions from '../../../Actions/commonActions'
import packageActions from '../../../Actions/Voss/packageActions'
import targetGroupActions from "../../../Actions/Voss/targetGroupActions";
import { UserRoles } from '../../../Constants/userRoles';
import useUserAccess from "../../../Hooks/useUserAccess";
import Card from '../../../Components/Card/Card';
import helpers from '../../../Utils/helpers';
import Grid from "../../../Components/Grid/Grid";
import { Checkbox } from "../../../Components/FormComponents/Checkbox";
import { RadioButtons } from "../../../Components/FormComponents/RadioButtons";
import SkynetIcon from "../../../Components/Fundamentals/SkynetIcon";
import {PackageSelector} from "../../../Components/FormComponents/PackageSelector";
import {FeatureFlags} from "../../../Utils/featureFlags";
import useFeatureFlags from "../../../Hooks/useFeatureFlags";
import {FormInput} from "../../../Components/FormComponents/Input/Input";
import {Button} from "../../../Components/Fundamentals/Button";
import ConfirmModal from "../../Modals/ConfirmModal";
import DateTimePicker from "../../../Components/DateTimePicker";
import EditScheduledTimeModal from "../../Modals/EditScheduledTimeModal";

function InvoicingContainer({ settingsActions, parameters, invoiceProcessings, pendingInvoiceProcessings, targetGroupActions, targetGroups, dataLoading, commonActions, packageActions, packages, products, preselections}) {

    useEffect(() => {
        if (!hasAdminAccess(UserRoles.Invoicing))
            return;
        settingsActions.getInvoiceProcessingSettings();
        if (!invoiceProcessings)
            settingsActions.getInvoiceProcessings();
        if (!pendingInvoiceProcessings)
            settingsActions.getPendingInvoiceProcessings();
        if (!targetGroups)
            targetGroupActions.getTargetGroups();
        if (!packages)
            packageActions.getPackageNames();
        if (!products)
            packageActions.getProductNames();
        settingsActions.getInvoicingPreselections();
    }, [])

    const {t} = useTranslation();
    const {isFeatureEnabled} = useFeatureFlags();
    const {hasAdminAccess} = useUserAccess();

    const [customerNumber, setCustomerNumber] = useState([]);
    const [confirmation, setConfirmation] = useState(null);
    const [editingPendingInvoicing, setEditingPendingInvoicing] = useState(null);
    const [preselectionName, setPreselectionName] = useState('');
    const [editingPreselection, setEditingPreselection] = useState(null);
    const [preselectionLocked, setPreselectionLocked] = useState(true);

    const createInvoicing = () => {
        if (!parameters.typeDebit && !parameters.typeCredit)
            commonActions.showWarning(t('invoiceprocessing.choosetype'));
        else if (!parameters.sourceNewPurchase && !parameters.sourceRenewal && !parameters.sourceUsage)
            commonActions.showWarning(t('invoiceprocessing.choosesource'));
        else if (parameters.showCustomerNumbers && parameters.customerNumbers.length === 0)
            commonActions.showWarning(t('invoiceprocessing.nocustomerselected'));
        else if (!parameters.showCustomerNumbers && !(parameters.onlyAO) && !(parameters.onlyAOS))
        {
            setConfirmation({
                onConfirmed: onCreateInvoicing,
                onCancel: () => setConfirmation(null),
                confirmTitle: t("invoiceprocessing.confirmcreateinvoicing"),
                confirmTextElement: <p>{t("invoiceprocessing.createforallcustomers")}<br/>{t("invoiceprocessing.continue")}</p>
            });
            commonActions.showModal(true);
        }
        else
            onCreateInvoicing();
    }

    const onCreateInvoicing = () => {
        settingsActions.createInvoiceProcessing(parameters,  targetGroups, () => setTimeout(refreshAllInvoiceProcessings, 3000));
        setConfirmation(null);
    }

    const refreshInvoiceProcessings = () => {
        settingsActions.storeInvoiceProcessings(null)
        settingsActions.getInvoiceProcessings();
    }

    const refreshPendingInvoiceProcessings = () => {
        settingsActions.storePendingInvoiceProcessings(null)
        settingsActions.getPendingInvoiceProcessings();
    }

    const refreshAllInvoiceProcessings = () => {
        refreshInvoiceProcessings();
        refreshPendingInvoiceProcessings();
    }

    const deleteInvoiceProcessing = (id) => {
        setConfirmation({
            onConfirmed: () => onDeleteInvoicing(id),
            onCancel: () => setConfirmation(null),
            confirmTitle: t("invoiceprocessing.confirmdeleteinvoicing"),
            confirmTextElement: <p>{t("invoiceprocessing.deletewarning")}</p>
        });
        commonActions.showModal(true);
    }

    const onDeleteInvoicing = (id) => {
        settingsActions.deleteInvoiceProcessing(id, () => {
            settingsActions.storeInvoiceProcessings(invoiceProcessings.filter(i => i.id !== id));
            setConfirmation(null);
        });
    }

    const deletePendingInvoiceProcessing = (id) => {
        setConfirmation({
            onConfirmed: () => onDeletePendingInvoicing(id),
            onCancel: () => setConfirmation(null),
            confirmTitle: t("invoiceprocessing.confirmdeleteinvoicing"),
            confirmTextElement: <p>{t("invoiceprocessing.deletewarning")}</p>
        });
        commonActions.showModal(true);
    }

    const onDeletePendingInvoicing = (id) => {
        settingsActions.deleteAsyncOperation(id, () => {
            settingsActions.storePendingInvoiceProcessings(pendingInvoiceProcessings.filter(i => i.id !== id));
            setConfirmation(null);
        });
    }

    const openEditModal = (pendingInvoicing) => {
        setEditingPendingInvoicing(pendingInvoicing);
        commonActions.showModal(true);
    }

    const handleSaveScheduledTime = (newScheduledTime, onComplete) => {
        settingsActions.updateAsyncOperation(editingPendingInvoicing.id, newScheduledTime, () => {
            const updated = pendingInvoiceProcessings.map(p => 
                p.id === editingPendingInvoicing.id ? {...p, scheduledStartTime: newScheduledTime} : p
            );
            settingsActions.storePendingInvoiceProcessings(updated);
            setEditingPendingInvoicing(null);
            if (onComplete) onComplete();
        });
    }

    const downloadInvoiceFile = (id) => {
        settingsActions.downloadInvoiceFile(id, `${id}.txt`);
    }

    const timeItems = [
        {label: t('invoiceprocessing.untilnow'), value: 'time'},
        {label: t('invoiceprocessing.date'), value: 'date'}
    ]

    const selectTimeOrDate = (value) => {
        if (value === 'date' && !parameters.invoiceDate)
            parameters.invoiceDate = helpers.getCurrentDate()
        settingsActions.storeInvoiceProcessingParameters({timeOrDate: value, invoiceDate: parameters.invoiceDate});
    }

    const changeInvoiceDate = (date) => {
        if (isFeatureEnabled(FeatureFlags.VSCO_2308_Future_dates) || new Date(date) <= new Date())
            settingsActions.storeInvoiceProcessingParameters({invoiceDate: date});
    }

    const changeScheduledStartTime = (date, dateObj) => {
        if (!dateObj) {
            settingsActions.storeInvoiceProcessingParameters({scheduledStartTime: null});
        } else if (dateObj > new Date()) {
            settingsActions.storeInvoiceProcessingParameters({scheduledStartTime: dateObj.toISOString()});
        }
    }

    const toggleCheckbox = (checked, id) => {
        settingsActions.storeInvoiceProcessingParameters({[id]: checked});
    }

    const addCustomerNumber = () => {
        parameters.customerNumbers.push(customerNumber);
        settingsActions.storeInvoiceProcessingParameters({customerNumbers: parameters.customerNumbers});
        setCustomerNumber("")
    }

    const clearCustomerNumbers = () => {
        settingsActions.storeInvoiceProcessingParameters({customerNumbers: []});
    }

    const removeCustomerNumber = (custNo) => {
        parameters.customerNumbers = parameters.customerNumbers.filter(cn => cn !== custNo);
        settingsActions.storeInvoiceProcessingParameters({customerNumbers: parameters.customerNumbers});
    }

    const selectPackage = (packageId) => {
        settingsActions.storeInvoiceProcessingParameters({packageId: packageId});
    }

    const selectProduct = (productId) => {
        settingsActions.storeInvoiceProcessingParameters({productId: productId});
    }

    const applyPreselection = (preselection) => {
        try {
            const {amountLimit, accumulationPeriodLength, accumulationPeriodUnit, timeOrDate, ...saved} = JSON.parse(preselection.parameters);
            const param = {
                showCustomerNumbers: false,
                ...saved
            };
            settingsActions.storeInvoiceProcessingParameters(param);
        } catch {
            commonActions.showWarning(t('invoiceprocessing.invalidpreselection'));
        }
    }

    const savePreselection = () => {
        if (!preselectionName.trim()) {
            commonActions.showWarning(t('invoiceprocessing.enterpreselectionname'));
            return;
        }
        const duplicate = preselections?.some(ps =>
            ps.name.toLowerCase() === preselectionName.trim().toLowerCase()
            && ps.id !== editingPreselection?.id);
        if (duplicate) {
            commonActions.showWarning(t('invoiceprocessing.duplicatepreselectionname'));
            return;
        }
        const {showCustomerNumbers, customerNumbers, amountLimit, accumulationPeriodLength, accumulationPeriodUnit, timeOrDate, ...paramsToSave} = parameters;
        const model = {
            name: preselectionName.trim(),
            parameters: JSON.stringify(paramsToSave)
        };
        if (editingPreselection) {
            settingsActions.updateInvoicingPreselection(editingPreselection.id, model, () => {
                settingsActions.getInvoicingPreselections();
                setPreselectionName('');
                setEditingPreselection(null);
            });
        } else {
            settingsActions.createInvoicingPreselection(model, () => {
                settingsActions.getInvoicingPreselections();
                setPreselectionName('');
            });
        }
    }

    const startEditPreselection = (preselection) => {
        setEditingPreselection(preselection);
        setPreselectionName(preselection.name);
        applyPreselection(preselection);
    }

    const deletePreselection = (preselection) => {
        setConfirmation({
            onConfirmed: () => {
                settingsActions.deleteInvoicingPreselection(preselection.id, () => {
                    settingsActions.getInvoicingPreselections();
                    setConfirmation(null);
                });
            },
            onCancel: () => setConfirmation(null),
            confirmTitle: t("invoiceprocessing.confirmdeletepreselection"),
            confirmTextElement: <p>{t("invoiceprocessing.deletepreselectionwarning")}</p>
        });
        commonActions.showModal(true);
    }

    const movePreselection = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= preselections.length) return;
        settingsActions.swapInvoicingPreselections(preselections[index].id, preselections[targetIndex].id, () => {
            settingsActions.getInvoicingPreselections();
        });
    }

    const cancelEditPreselection = () => {
        setEditingPreselection(null);
        setPreselectionName('');
    }

    const renderCustomerNumbers = () => {
        if (parameters.showCustomerNumbers)
            return <>
                <div className="d-flex flex-row gap-10" style={{marginLeft: 20}}>
                    <FormInput
                           onChange={setCustomerNumber}
                           value={customerNumber}
                           onKeyDown={e => {
                               if (e.key === "Enter") addCustomerNumber()
                           }}/>

                    <Button onClick={addCustomerNumber}>
                        {t("invoiceprocessing.add")}
                    </Button>
                    <Button onClick={clearCustomerNumbers}>
                        {t("invoiceprocessing.clear")}
                    </Button>
                </div>
                <table className="table table-hover" style={{marginLeft: 20}}>
                    <tbody>
                    {p.customerNumbers.map((custNo, index) => {
                        return <tr key={`${index}${custNo}`}>
                        <td>{custNo}</td>
                            <td>
                                <Button onClick={() => removeCustomerNumber(custNo)}>
                                    {t("invoiceprocessing.remove")}
                                </Button>
                            </td>
                        </tr>
                    })}
                    </tbody>
                </table>
            </>
        return <></>
    }
    const featTS = isFeatureEnabled(FeatureFlags.VOSS_2308_Invoicing_TransactionSource)

    const renderPendingInvoiceTable = (pendingInvoiceProcessings) => {
        if (!pendingInvoiceProcessings)
            return <div className="spinner spinner-default-green m-auto d-block"/>;
        if (pendingInvoiceProcessings.length === 0)
            return <div className="text-center p-3">{t("invoiceprocessing.noPending")}</div>;
        
        const sortedPendingInvoiceProcessings = [...pendingInvoiceProcessings].sort((a, b) => {
            return new Date(b.scheduledStartTime) - new Date(a.scheduledStartTime);
        });

        return <div className="table-responsive">
            <table className="table table-hover">
                <thead>
                <tr>
                    <th>{t("invoiceprocessing.scheduledStartTime")}</th>
                    <th>{t("invoiceprocessing.invoicedate")}</th>
                    {featTS ? <th>System</th> : null}
                    <th>{t("invoiceprocessing.type")}</th>
                    <th>{t("invoiceprocessing.source")}</th>
                    <th>{t("invoiceprocessing.selection")}</th>
                    <th>{t("invoiceprocessing.package")}</th>
                    <th>{t("invoiceprocessing.edit")}</th>
                    <th>{t("invoiceprocessing.cancel")}</th>
                    <th>{t("invoiceprocessing.createdby")}</th>
                </tr>
                </thead>
                <tbody>
                {
                    sortedPendingInvoiceProcessings.map((pendingInvoicing, index) => {
                        return <tr key={index}>
                            <td>{helpers.formatDateAndTime(pendingInvoicing.scheduledStartTime)}</td>
                            <td>{helpers.formatDateAndTime(pendingInvoicing.invoiceDate)}</td>
                            {featTS ? <td>{pendingInvoicing.transactionSources?.join(", ") || ""}</td> : null}
                            <td>{pendingInvoicing.transactionTypesKeys?.map(k => t(k)).join(", ") || ""}</td>
                            <td>{pendingInvoicing.transactionSourceTypesKeys?.map(k => t(k)).join(", ") || ""}</td>
                            <td>
                                {pendingInvoicing.customerSelectionKeys?.length > 0
                                    ? pendingInvoicing.customerSelectionKeys.map(k =>
                                        k.startsWith("invoiceprocessing.") ? t(k) : k
                                    ).join(", ")
                                    : ""
                                }
                            </td>
                            <td>{pendingInvoicing.packageNames?.join(", ") || ""}</td>
                            <td>
                                <SkynetIcon icon="vismaicon-edit" onClick={() => openEditModal(pendingInvoicing)}/>
                            </td>
                            <td>
                                <SkynetIcon icon="vismaicon-delete" onClick={() => deletePendingInvoiceProcessing(pendingInvoicing.id)}/>
                            </td>
                            <td>{pendingInvoicing.createdBy}</td>
                        </tr>
                    })
                }
                </tbody>
            </table>
        </div>
    }

    const renderInvoiceTable = (invoiceProcessings) => {
        if (!invoiceProcessings)
            return <div className="spinner spinner-default-green m-auto d-block"/>;
        return <div className="table-responsive">
            <table className="table table-hover">
                <thead>
                <tr>
                    <th>{t("invoiceprocessing.invoicedate")}</th>
                    {featTS ? <th>System</th> : null}
                    <th>{t("invoiceprocessing.totalsum")}</th>
                    <th>{t("invoiceprocessing.type")}</th>
                    <th>{t("invoiceprocessing.source")}</th>
                    <th>{t("invoiceprocessing.selection")}</th>
                    <th>{t("invoiceprocessing.package")}</th>
                    <th>{t("invoiceprocessing.regret")}</th>
                    <th>{t("invoiceprocessing.downloadfile")}</th>
                    <th>{t("invoiceprocessing.createdby")}</th>
                </tr>
                </thead>
                <tbody>
                {
                    invoiceProcessings.map((invoiceProcessing, index) => {
                        return <tr key={index}>
                                <td>{helpers.formatDateAndTime(invoiceProcessing.invoiceDate)}</td>
                                {featTS ? <td>{invoiceProcessing.transactionSources?.join(", ") || ""}</td> : null}
                                <td>{invoiceProcessing.totalAmount}</td>
                                <td>{invoiceProcessing.transactionTypesKeys?.map(k => t(k)).join(", ") || ""}</td>
                                <td>{invoiceProcessing.transactionSourceTypesKeys?.map(k => t(k)).join(", ") || ""}</td>
                                <td>
                                    {invoiceProcessing.customerSelectionKeys?.length > 0
                                        ? invoiceProcessing.customerSelectionKeys.map(k =>
                                            k.startsWith("invoiceprocessing.") ? t(k) : k
                                        ).join(", ")
                                        : ""
                                    }
                                </td>
                                <td>{invoiceProcessing.packageNames?.join(", ") || ""}</td>
                                <td>
                                    <SkynetIcon icon="vismaicon-delete" onClick={() => deleteInvoiceProcessing(invoiceProcessing.id)}/>
                                </td>
                                <td>
                                    {
                                        dataLoading && dataLoading[`download_${invoiceProcessing.id}`] === true ?
                                            <div style={{maxWidth: 25, maxHeight: 25}} className="spinner spinner-default-green m-auto"/> :
                                            <SkynetIcon icon="vismaicon-download"
                                                        onClick={() => downloadInvoiceFile(invoiceProcessing.id)}/>
                                    }
                                </td>
                                <td>{invoiceProcessing.createdBy}</td>
                            </tr>
                        })
                    }
                    </tbody>
                </table>
            </div>
        }

        const p = parameters;

        return <Grid>
            <Grid.Row>
                <Grid.Row.Column width={3}>
                    <Card>
                        <Card.Header>
                            <Card.Header.Title>{t("invoiceprocessing.createinvoicing")}</Card.Header.Title>
                        </Card.Header>
                        <Card.Content>

                            <fieldset className="d-grid col-12 gap-8">
                                <label><b>{t("invoiceprocessing.scheduledmonthlyinvoicing")}</b></label>
                                <div className="col-6 p-0">
                                <DateTimePicker
                                    showTime={true}
                                    closeOnSelect={false}
                                    onChange={changeScheduledStartTime}
                                    value={p.scheduledStartTime}
                                    isValidDate={(current) => current.isAfter(moment())}
                                    showTimezone={true}/>
                                </div>
                            </fieldset>

                            <fieldset className="d-grid col-6 gap-8">
                                <RadioButtons items={timeItems} label={<b>{t("invoiceprocessing.invoicedate")}</b>}
                                              onChange={selectTimeOrDate} value={p.timeOrDate}/>
                                {p.timeOrDate === 'date' ?
                                    <div style={{width: 300, marginTop: -15}}>
                                        <DateTimePicker showTime={false} closeOnSelect={true}
                                                       onChange={changeInvoiceDate} value={p.invoiceDate}/>
                                    </div>
                                    : <></>}
                            </fieldset>
                            <fieldset className="d-grid col gap-8">
                                <div className="d-flex justify-content-between align-items-center">
                                    <label><b>{t("invoiceprocessing.preselections")}</b></label>
                                    <SkynetIcon icon={preselectionLocked ? "vismaicon-lock" : "vismaicon-unlock"}
                                                onClick={() => setPreselectionLocked(!preselectionLocked)}/>
                                </div>
                                <div className="d-flex flex-column gap-4">
                                    {preselections?.map((ps, index) =>
                                        <div key={ps.id} className="d-flex align-items-center gap-4">
                                            <Button onClick={() => applyPreselection(ps)}>{ps.name}</Button>
                                            {!preselectionLocked && <>
                                                <SkynetIcon icon="vismaicon-edit" onClick={() => startEditPreselection(ps)}/>
                                                <SkynetIcon icon="vismaicon-delete" onClick={() => deletePreselection(ps)}/>
                                                <SkynetIcon icon="vismaicon-move-up-circle" onClick={() => movePreselection(index, -1)}/>
                                                <SkynetIcon icon="vismaicon-move-down-circle" onClick={() => movePreselection(index, 1)}/>
                                            </>}
                                        </div>
                                    )}
                                </div>
                                {!preselectionLocked &&
                                    <div className="d-flex flex-row gap-10 align-items-center">
                                        <FormInput
                                               placeholder={t("invoiceprocessing.preselectionname")}
                                               value={preselectionName}
                                               onChange={setPreselectionName}/>
                                        <Button onClick={savePreselection}>
                                            {editingPreselection ? t("invoiceprocessing.updatepreselection") : t("invoiceprocessing.savepreselection")}
                                        </Button>
                                        {editingPreselection &&
                                            <Button onClick={cancelEditPreselection}>
                                                {t("invoiceprocessing.cancel")}
                                            </Button>}
                                    </div>}
                            </fieldset>

                            <div className="d-flex flex-row">
                            <fieldset className="d-grid col-6 gap-8">
                                    <label><b>{t("invoiceprocessing.type")}</b></label>
                                    <Checkbox id="typeDebit" onChange={toggleCheckbox}
                                              label={t("invoiceprocessing.Debit")} checked={p.typeDebit}/>
                                    <Checkbox id="typeCredit" onChange={toggleCheckbox}
                                              label={t("invoiceprocessing.Credit")} checked={p.typeCredit}/>
                                </fieldset>
                                <fieldset className="d-grid col-6 gap-8">
                                    <label><b>{t("invoiceprocessing.source")}</b></label>
                                    <Checkbox id="sourceNewPurchase" onChange={toggleCheckbox}
                                              label={t("invoiceprocessing.NewPurchase")} checked={p.sourceNewPurchase}/>
                                    <Checkbox id="sourceRenewal" onChange={toggleCheckbox}
                                              label={t("invoiceprocessing.Renewal")} checked={p.sourceRenewal}/>
                                    <Checkbox id="sourceUsage" onChange={toggleCheckbox}
                                              label={t("invoiceprocessing.Usage")} checked={p.sourceUsage}/>
                                </fieldset>
                            </div>

                            <fieldset className="d-grid col gap-8">
                                <label><b>{t("invoiceprocessing.selection")}</b></label>
                                <Checkbox id="onlyAO" onChange={toggleCheckbox}
                                          label={t("invoiceprocessing.accountingoffice")} checked={p.onlyAO}/>
                                <Checkbox id="onlyAOS" onChange={toggleCheckbox}
                                          label={t("invoiceprocessing.aosimplicity")} checked={p.onlyAOS}/>
                                <Checkbox id="normalCustomers" onChange={toggleCheckbox}
                                          label={t("invoiceprocessing.normalcustomers")} checked={p.normalCustomers}/>

                                <Checkbox id="showPackages" onChange={toggleCheckbox}
                                          label={t("invoiceprocessing.package")}
                                          checked={p.showPackages}/>
                                {p.showPackages && packages ?
                                    <PackageSelector packages={packages} onChange={selectPackage} defaultPackageId={p.packageId}></PackageSelector> : <></>}

                                <Checkbox id="showProducts" onChange={toggleCheckbox}
                                          label={t("invoiceprocessing.product")}
                                          checked={p.showProducts}/>
                                {p.showProducts && products ?
                                    <PackageSelector packages={products} onChange={selectProduct} defaultPackageId={p.productId}></PackageSelector> : <></>}

                                <Checkbox id="showCustomerNumbers" onChange={toggleCheckbox}
                                               label={t("invoiceprocessing.customernumbers")}
                                               checked={p.showCustomerNumbers}/>
                                {renderCustomerNumbers()}
                            </fieldset>

                            <fieldset className="d-grid col gap-8">
                                <label><b>{t("invoiceprocessing.amountlimit")}</b></label>
                                <Checkbox id="amountLimitSelected" onChange={toggleCheckbox}
                                          label={`${t("invoiceprocessing.amountlimit")} ${p.amountLimit} ${t("invoiceprocessing.sek")} (${p.accumulationPeriodLength}, ${p.accumulationPeriodUnit})`} checked={p.amountLimitSelected}/>
                                {/*<Checkbox id="accumulated3months" onChange={toggleCheckbox}*/}
                                {/*          label={t("invoiceprocessing.accumulated3months")} checked={p.accumulated3months}/>*/}
                            </fieldset>

                            {featTS ?
                            <fieldset className="d-grid col gap-8">
                                <label><b>Transaction Source</b></label>
                                <Checkbox id="tranSourceVoss" onChange={toggleCheckbox}
                                          label="Voss" checked={p.tranSourceVoss}/>
                                <Checkbox id="tranSourceVon" onChange={toggleCheckbox}
                                          label="Von" checked={p.tranSourceVon}/>
                            </fieldset> : <></>}

                            <div>
                                <Button onClick={() => createInvoicing()}>{t("invoiceprocessing.createinvoicing")}</Button>

                                {confirmation ? <ConfirmModal {...confirmation}
                                              buttonTexts={[t("invoiceprocessing.yes"), t("invoiceprocessing.cancel")]}/> : <> </>}
                                
                                {editingPendingInvoicing ? (
                                    <EditScheduledTimeModal
                                        currentScheduledTime={editingPendingInvoicing.scheduledStartTime}
                                        onClose={() => setEditingPendingInvoicing(null)}
                                        onSave={handleSaveScheduledTime}
                                    />
                                ) : null}
                            </div>

                        </Card.Content>
                    </Card>
                </Grid.Row.Column>

                <Grid.Row.Column>
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between w-100">
                                <Card.Header.Title>{t("invoiceprocessing.pendingInvoicings")}</Card.Header.Title>
                                <SkynetIcon icon="vismaicon-refresh" onClick={refreshPendingInvoiceProcessings}/>
                            </div>
                        </Card.Header>
                        <Card.Content>
                        {renderPendingInvoiceTable(pendingInvoiceProcessings)}
                        </Card.Content>
                    </Card>

                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between w-100">
                                <Card.Header.Title>{t("invoiceprocessing.invoicings")}</Card.Header.Title>
                                <SkynetIcon icon="vismaicon-refresh" onClick={refreshInvoiceProcessings}/>
                            </div>
                        </Card.Header>
                        <Card.Content>
                        {renderInvoiceTable(invoiceProcessings)}
                        </Card.Content>
                    </Card>
                </Grid.Row.Column>
            </Grid.Row>
        </Grid>
}

function mapStateToProps(state) {
    return {
        invoiceProcessings: state.SettingsReducer.invoiceProcessings,
        pendingInvoiceProcessings: state.SettingsReducer.pendingInvoiceProcessings,
        targetGroups: state.TargetGroupsReducer.targetGroups,
        dataLoading: state.CommonReducer.dataLoading,
        parameters: state.SettingsReducer.invoiceProcessingParameters,
        preselections: state.SettingsReducer.invoicingPreselections,
        packages: state.PackageReducer.packageNames,
        products: state.PackageReducer.productNames,
        showModal: state.CommonReducer.showModal
    }
}

function mapDispatchToProps(dispatch) {
    return {
        settingsActions: bindActionCreators(settingsActions, dispatch),
        commonActions: bindActionCreators(commonActions, dispatch),
        targetGroupActions: bindActionCreators(targetGroupActions, dispatch),
        packageActions: bindActionCreators(packageActions, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(InvoicingContainer));